// Файл: NATR.js

/**
 * Вспомогательный класс для Average True Range (ATR)
 * Использует сглаживание Уайлдера (Wilder's Smoothing).
 */
class ATR {
  /**
   * @param {number} period Период для расчета ATR
   */
  constructor(period) {
    if (period <= 0) throw new Error('Период должен быть положительным числом');
    this.period = period;
    this.alpha = 1 / period;
    this.trHistory = []; // Для расчета первого значения ATR
    this.lastAtr = null;
  }

  /**
   * Вычисляет следующее значение ATR.
   * @param {number} tr Текущее значение True Range
   * @returns {number|null} Следующее значение ATR или null, если данных недостаточно
   */
  nextValue(tr) {
    if (this.lastAtr === null) {
      this.trHistory.push(tr);
      // Если данных для первого расчета еще не хватает
      if (this.trHistory.length < this.period) {
        return null;
      }
      // Рассчитываем первое значение ATR как простое среднее
      const sum = this.trHistory.reduce((a, b) => a + b, 0);
      this.lastAtr = sum / this.period;
    } else {
      // Последующие значения рассчитываются по формуле сглаживания
      // Формула (lastAtr * (period - 1) + tr) / period эквивалентна EMA с alpha = 1/period
      this.lastAtr = this.alpha * tr + (1 - this.alpha) * this.lastAtr;
    }
    return this.lastAtr;
  }
  
  /**
   * Вычисляет гипотетическое следующее значение ATR без изменения состояния.
   * @param {number} tr Текущее значение True Range
   * @returns {number|null} Гипотетическое следующее значение ATR или null
   */
  momentValue(tr) {
    if (this.lastAtr === null) {
      if (this.trHistory.length < this.period - 1) {
        return null; // Невозможно рассчитать даже гипотетически
      }
       const momentHistory = [...this.trHistory, tr];
       const sum = momentHistory.reduce((a, b) => a + b, 0);
       return sum / this.period;
    }
    return this.alpha * tr + (1 - this.alpha) * this.lastAtr;
  }
}


/**
 * Normalized Average True Range (NATR)
 * Индикатор измеряет волатильность в виде процента от цены закрытия.
 */
export class NATR {
  /**
   * @param {number} period Период для расчета ATR, по умолчанию 14
   */
  constructor(period = 14) {
    this.atr = new ATR(period);
    this.previousClose = null;
  }

  /**
   * Вычисляет следующее значение NATR на основе новой свечи.
   * @param {number} h High (максимум)
   * @param {number} l Low (минимум)
   * @param {number} c Close (цена закрытия)
   * @returns {number|null} Следующее значение NATR или null, если данных недостаточно
   */
  nextValue(h, l, c) {
    // Если предыдущего закрытия нет, это первая свеча. Мы не можем рассчитать TR.
    if (this.previousClose === null) {
      this.previousClose = c;
      return null;
    }

    // 1. Рассчитываем True Range (TR)
    const tr1 = h - l;
    const tr2 = Math.abs(h - this.previousClose);
    const tr3 = Math.abs(l - this.previousClose);
    const trueRange = Math.max(tr1, tr2, tr3);
    
    // 2. Получаем следующее значение ATR
    const atrValue = this.atr.nextValue(trueRange);
    
    // Обновляем previousClose для следующего вызова
    this.previousClose = c;
    
    // Если ATR еще не рассчитан (недостаточно данных)
    if (atrValue === null) {
        return null;
    }

    // 3. Рассчитываем NATR
    // Проверка деления на ноль для надежности
    if (c === 0) {
        return 0;
    }
    return (atrValue / c) * 100;
  }
  
  /**
   * Вычисляет "мгновенное" значение для текущей, еще не закрытой свечи.
   * @param {number} h High (максимум)
   * @param {number} l Low (минимум)
   * @param {number} c Close (цена закрытия)
   * @returns {number|null} Мгновенное значение NATR или null
   */
  momentValue(h, l, c) {
     if (this.previousClose === null) {
      return null;
    }
    const tr1 = h - l;
    const tr2 = Math.abs(h - this.previousClose);
    const tr3 = Math.abs(l - this.previousClose);
    const trueRange = Math.max(tr1, tr2, tr3);
    
    const atrValue = this.atr.momentValue(trueRange);

    if (atrValue === null) {
        return null;
    }

    if (c === 0) {
        return 0;
    }
    return (atrValue / c) * 100;
  }
}

// Для использования в Node.js
// module.exports = { NATR };